import React, { useEffect, useState } from 'react';
import { X, Download, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

interface ImageZoomModalProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = ({ imageUrl, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setScale(1);
      setRotation(0);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleRotate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `xleven-image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-[110]">
        <div className="flex items-center gap-2">
          <button 
            onClick={handleZoomIn}
            className="p-2 text-white/70 bg-black/20 rounded-full transition-all backdrop-blur-sm"
            title="Zoom In"
          >
            <ZoomIn size={20} />
          </button>
          <button 
            onClick={handleZoomOut}
            className="p-2 text-white/70 bg-black/20 rounded-full transition-all backdrop-blur-sm"
            title="Zoom Out"
          >
            <ZoomOut size={20} />
          </button>
          {/* <button 
            onClick={handleRotate}
            className="p-2 text-white/70 bg-black/20 rounded-full transition-all backdrop-blur-sm"
            title="Rotate"
          >
            <RotateCw size={20} />
          </button>
          <button 
            onClick={handleDownload}
            className="p-2 text-white/70 bg-black/20 rounded-full transition-all backdrop-blur-sm"
            title="Download"
          >
            <Download size={20} />
          </button> */}
        </div>
        
        <button 
          onClick={onClose}
          className="p-2 text-white/70 bg-black/20 rounded-full transition-all backdrop-blur-sm"
        >
          <X size={24} />
        </button>
      </div>

      {/* Image Container */}
      <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12 overflow-hidden">
        <img 
          src={imageUrl} 
          alt="Enlarged view" 
          className="max-w-full max-h-full object-contain transition-transform duration-300 ease-out shadow-2xl"
          style={{ 
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            cursor: scale > 1 ? 'zoom-out' : 'zoom-in'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (scale > 1) setScale(1);
            else setScale(1.5);
          }}
        />
      </div>
    </div>
  );
};

export default ImageZoomModal;
